import { useState, useEffect } from 'react';
import { useDashboardAuth } from '../../hooks/useDashboardAuth';
import { LoginPage } from './LoginPage';
import { MissionControlPage } from './MissionControlPage';
import { CaseViewPage } from './CaseViewPage';
import { StipulationEditorPage } from './StipulationEditorPage';
import { ReportsPage } from './ReportsPage';
import { AvailabilityPage } from './AvailabilityPage';
import { ClerkDocumentsPage } from './ClerkDocumentsPage';
import { AdminPage } from './AdminPage';
import { PracticeSettingsPage } from './PracticeSettingsPage';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import type { DashboardSession } from '../../lib/dashboardSession';
import type { Principal } from '../../types/dashboard';

export type DashboardView =
  | { name: 'mission' }
  | { name: 'case'; caseId: string }
  | { name: 'stipulation'; caseId: string; agreementId: string; caseTitle?: string | null }
  | { name: 'availability' }
  | { name: 'documents' }
  | { name: 'admin' }
  | { name: 'practice' }
  | { name: 'reports' };

/** While impersonating, the admin's own session is stashed here so Exit can
    restore it. sessionStorage on purpose: an impersonation never outlives
    the tab. */
const IMPERSONATION_STASH_KEY = 'keel_impersonation_original';

function loadStashedSession(): DashboardSession | null {
  try {
    const raw = window.sessionStorage.getItem(IMPERSONATION_STASH_KEY);
    return raw ? (JSON.parse(raw) as DashboardSession) : null;
  } catch {
    return null;
  }
}

/** UI capability flags derived from the signed-in principal. The Engine
    enforces all of this server-side too — these only shape what we render. */
export interface Caps {
  role: string;
  isClerk: boolean;
  /** Admin mediator (Danny, 2026-10-19): owns pre-mediation comms across their
      partners; their queue defaults to un-scheduled cases. */
  isAdminMediator: boolean;
  /** judge_read_only — every mutating control is hidden/disabled. */
  readOnly: boolean;
  /** May send messages, release drafts, draft documents, nudge scheduling. */
  canMutate: boolean;
}

export function capsFor(principal: Principal): Caps {
  const role = principal.role ?? 'mediator';
  const isClerk = role === 'clerk';
  const isAdminMediator = role === 'admin_mediator';
  const readOnly = principal.judge_read_only === true;
  return { role, isClerk, isAdminMediator, readOnly, canMutate: !isClerk && !readOnly };
}

interface Props {
  /** True when rendering inside the Zoom main client (vs a browser tab). */
  inZoomClient: boolean;
}

/**
 * The Levelheaded dashboard: Mission Control + Case view (+ clerk documents,
 * mediator availability). Rendered in the Zoom main client and in ordinary
 * browser tabs; the in-meeting sidebar never routes here.
 */
export function DashboardApp({ inZoomClient }: Props) {
  const auth = useDashboardAuth();
  const [view, setView] = useState<DashboardView>(() => {
    // Deep links from the Zoom sidebar's "Open full editor" pop-out
    // (Danny, 2026-09-25): ?open_case=<id>[&agreement=<id>] lands the
    // mediator straight where they were working.
    try {
      const params = new URLSearchParams(window.location.search);
      const caseId = params.get('open_case');
      const agreementId = params.get('agreement');
      if (caseId && agreementId) return { name: 'stipulation', caseId, agreementId, caseTitle: null };
      if (caseId) return { name: 'case', caseId };
    } catch { /* fall through */ }
    return { name: 'mission' };
  });
  useEffect(() => {
    // Clean the deep-link params so refresh/back behave normally.
    if (window.location.search.includes('open_case')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  const [stashedAdmin, setStashedAdmin] = useState<DashboardSession | null>(loadStashedSession);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const session = auth.state.status === 'idle' ? auth.state.session : null;

  const startImpersonation = (res: { token: string; expires_in: number; principal: Principal }) => {
    if (!session) return;
    try {
      window.sessionStorage.setItem(IMPERSONATION_STASH_KEY, JSON.stringify(session));
    } catch {
      /* still usable; Exit will just sign out */
    }
    setStashedAdmin(session);
    auth.adoptSession({
      token: res.token,
      expiresAt: Date.now() + res.expires_in * 1000,
      principal: res.principal,
    });
    setView({ name: 'mission' });
  };

  const exitImpersonation = () => {
    try {
      window.sessionStorage.removeItem(IMPERSONATION_STASH_KEY);
    } catch {
      /* ignore */
    }
    const original = stashedAdmin;
    setStashedAdmin(null);
    if (original && original.expiresAt > Date.now()) {
      auth.adoptSession(original);
      setView({ name: 'admin' });
    } else {
      auth.signOut(); // admin token expired mid-impersonation — re-login
    }
  };

  if (!session) {
    return (
      <LoginPage
        signingIn={auth.state.status === 'signing-in'}
        error={auth.state.status === 'error' ? auth.state.error : undefined}
        onSignIn={(email, password) => void auth.signIn(email, password)}
      />
    );
  }

  const { principal, token } = session;

  if (principal.type !== 'mediator') {
    return (
      <div className="flex min-h-full flex-col items-center justify-center p-6 text-center">
        <h2 className="text-base font-semibold">This workspace is for mediation teams</h2>
        <p className="mt-2 max-w-sm text-sm text-gray-600">
          Partner accounts have a separate summary view. Sign in with a mediator, admin, or
          clerk account to use the Levelheaded dashboard.
        </p>
        <button type="button" className="btn-secondary mt-4" onClick={auth.signOut}>
          Sign out
        </button>
      </div>
    );
  }

  const caps = capsFor(principal);
  const openCase = (caseId: string) => setView({ name: 'case', caseId });

  return (
    <div className="flex min-h-full flex-col bg-surface-muted">
      <header className="sticky top-0 z-10 border-b border-surface-border bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-2.5">
          <button
            type="button"
            className="text-lg font-bold tracking-tight text-brand"
            onClick={() => setView({ name: 'mission' })}
          >
            Levelheaded
          </button>
          <nav className="flex items-center gap-1 text-sm">
            <NavButton
              active={view.name === 'mission' || view.name === 'case'}
              label={caps.isClerk ? 'Cases' : 'Mission Control'}
              onClick={() => setView({ name: 'mission' })}
            />
            {caps.isClerk && (
              <NavButton
                active={view.name === 'documents'}
                label="Documents"
                onClick={() => setView({ name: 'documents' })}
              />
            )}
            {!caps.isClerk && (
              <NavButton
                active={view.name === 'availability'}
                label="Availability"
                onClick={() => setView({ name: 'availability' })}
              />
            )}
            {!caps.isClerk && (
              <NavButton
                active={view.name === 'reports'}
                label="Reports"
                onClick={() => setView({ name: 'reports' })}
              />
            )}
            {caps.role === 'admin' && !principal.mediator_group_id && (
              <NavButton
                active={view.name === 'admin'}
                label="Admin"
                onClick={() => setView({ name: 'admin' })}
              />
            )}
            {(caps.role === 'admin' || caps.role === 'admin_mediator') && principal.mediator_group_id && (
              <NavButton
                active={view.name === 'practice'}
                label="My practice"
                onClick={() => setView({ name: 'practice' })}
              />
            )}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {caps.readOnly && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                Read-only
              </span>
            )}
            <span className="hidden text-xs text-gray-500 sm:inline">
              {principal.display_name}
              <span className="ml-1 text-gray-400">({caps.role})</span>
            </span>
            {!stashedAdmin && (
              <button
                type="button"
                className="hidden text-xs text-gray-500 hover:text-gray-800 sm:inline"
                onClick={() => setShowChangePassword(true)}
              >
                Change password
              </button>
            )}
            <button
              type="button"
              className="btn-secondary !py-1 !text-xs"
              onClick={() => {
                try {
                  window.sessionStorage.removeItem(IMPERSONATION_STASH_KEY);
                } catch {
                  /* ignore */
                }
                setStashedAdmin(null);
                auth.signOut();
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {stashedAdmin && (
        <div className="sticky top-[49px] z-10 border-b border-amber-200 bg-amber-50">
          <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-1.5 text-xs text-amber-900">
            <span>
              Viewing as <strong>{principal.display_name}</strong> — every action is audited under
              both identities.
            </span>
            <button
              type="button"
              className="ml-auto rounded-md border border-amber-300 px-2 py-0.5 font-medium hover:bg-amber-100"
              onClick={exitImpersonation}
            >
              Exit impersonation
            </button>
          </div>
        </div>
      )}

      <main className="mx-auto w-full max-w-6xl flex-1 p-4">
        {view.name === 'mission' && (
          <MissionControlPage
            token={token}
            caps={caps}
            selfId={principal.id}
            selfName={principal.display_name}
            onOpenCase={openCase}
            onUnauthorized={auth.onUnauthorized}
          />
        )}
        {view.name === 'case' && (
          <CaseViewPage
            token={token}
            caseId={view.caseId}
            caps={caps}
            inZoomClient={inZoomClient}
            onBack={() => setView({ name: 'mission' })}
            onOpenStipEditor={(agreementId, caseTitle) =>
              setView({ name: 'stipulation', caseId: view.caseId, agreementId, caseTitle })
            }
            onUnauthorized={auth.onUnauthorized}
          />
        )}
        {view.name === 'stipulation' && (
          <StipulationEditorPage
            token={token}
            agreementId={view.agreementId}
            caseId={view.caseId}
            caseTitle={view.caseTitle}
            onBack={() => setView({ name: 'case', caseId: view.caseId })}
            onUnauthorized={auth.onUnauthorized}
          />
        )}
        {view.name === 'availability' && !caps.isClerk && (
          <AvailabilityPage token={token} caps={caps} onUnauthorized={auth.onUnauthorized} />
        )}
        {view.name === 'reports' && !caps.isClerk && (
          <ReportsPage token={token} onUnauthorized={auth.onUnauthorized} />
        )}
        {view.name === 'practice' && (
          <PracticeSettingsPage token={token} onUnauthorized={auth.onUnauthorized} />
        )}
        {view.name === 'documents' && caps.isClerk && (
          <ClerkDocumentsPage token={token} onOpenCase={openCase} onUnauthorized={auth.onUnauthorized} />
        )}
        {view.name === 'admin' && caps.role === 'admin' && (
          <AdminPage
            token={token}
            selfId={principal.id}
            onImpersonate={startImpersonation}
            onUnauthorized={auth.onUnauthorized}
          />
        )}
      </main>

      {showChangePassword && (
        <ChangePasswordDialog token={token} onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

function NavButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'rounded-md px-2.5 py-1.5 font-medium transition-colors',
        active ? 'bg-brand-subtle text-brand' : 'text-gray-600 hover:text-gray-900',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
